import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
  , handleAddManyToList
} from '../../global/utils/storeUtils';


// First define all API calls for referentCollection

// define and export the strings for the different specific referentCollection endpoints once here because the idea of using strings in the component gives me hives.
// we'll catch for these strings on the server side and apply the correct permissions to the query.
// these are passed in to the referentCollectionService hooks at the component level as the endpoint argument.
// export const myExampleEndpoint = 'logged-in';
/**
 * The functions below, called thunks, allow us to perform async logic. They
 * can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
 * will call the thunk with the `dispatch` function as the first argument. Async
 * code can then be executed and other actions can be dispatched. Thunks are
 * typically used to make async requests.
 * 
 * In practice we won't dispatch these directly, they will be dispatched by referentCollectionService which has a nicer api built on hooks.
 */

// CREATE
export const sendCreateReferentCollection = createAsyncThunk(
  'referentCollection/sendCreate'
  , async (newReferentCollection) => {
    const endpoint = `/api/referent-collections`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newReferentCollection);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// READ
export const fetchSingleReferentCollection = createAsyncThunk(
  'referentCollection/fetchSingle'
  , async (id) => {
    const endpoint = `/api/referent-collections/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchReferentCollectionList = createAsyncThunk(
  'referentCollection/fetchList' // this is the action name that will show up in the console logger.
  , async (listArgs) => {
    const endpoint = `/api/referent-collections${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// for each resource we can add as many endpoints as we want in this format and we only need two actions to handle them.
// this will hit the same endpoint as the list version, but the store will handle the returned array and access the single item in it.
export const fetchSingleReferentCollectionAtEndpoint = createAsyncThunk(
  'referentCollection/fetchSingleWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/referent-collections${query}` // example: `/api/referent-collections/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchReferentCollectionListAtEndpoint = createAsyncThunk(
  'referentCollection/fetchListWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/referent-collections${query}`; // example: `/api/referent-collections/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// UPDATE
export const sendUpdateReferentCollection = createAsyncThunk(
  'referentCollection/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/referent-collections/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const sendUploadReferentsToCollection = createAsyncThunk(
  'referentCollection/sendUploadReferents'
  , async ({ id, file }) => {
    const endpoint = `/api/referent-collections/${id}/import-from-csv`;
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiUtils.rawAPI(endpoint, 'POST', formData);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// DELETE
export const sendDeleteReferentCollection = createAsyncThunk(
  'referentCollection/sendDelete'
  , async (id) => {
    const endpoint = `/api/referent-collections/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// next define the store's initial state, all of our store utils rely on a specific state shape, so use the constant
const initialState = { ...INITIAL_STATE };

// define the referentCollectionSlice. This is a combination of actions and reducers. More info: https://redux-toolkit.js.org/api/createSlice
export const referentCollectionSlice = createSlice({
  name: 'referentCollection'
  , initialState
  /**
   * The `reducers` field lets us define reducers and generate associated actions.
   * Unlike the selectors defined at the bottom of this file, reducers only have access
   * to this specific reducer and not the entire store.
   * 
   * Again, we will not dispatch these directly, they will be dispatched by referentCollectionService.
   */
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addReferentCollectionToList: handleAddSingleToList
    , addReferentCollectionsToList: handleAddManyToList
  }

  /**
   * The `extraReducers` field lets the slice handle actions defined elsewhere,
   * including actions generated by createAsyncThunk or in other slices.
   * We'll use them to track our server request status.
   * 
   * We'll add a case for each API call defined at the top of the file to dictate
   * what happens during each API call lifecycle.
   */
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateReferentCollection.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleReferentCollection.pending, handleFetchSinglePending)
      .addCase(fetchSingleReferentCollection.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleReferentCollection.rejected, handleFetchSingleRejected)
      .addCase(fetchReferentCollectionList.pending, handleFetchListPending)
      // because lists are returned from the server named for their resource, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchReferentCollectionList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'referentCollections'))
      .addCase(fetchReferentCollectionList.rejected, handleFetchListRejected)

      // permission protected single fetches
      .addCase(fetchSingleReferentCollectionAtEndpoint.pending, handleFetchSinglePending)
      // these endpoints return named lists, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchSingleReferentCollectionAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'referentCollections'))
      .addCase(fetchSingleReferentCollectionAtEndpoint.rejected, handleFetchSingleRejected)
      // permission protected list fetches
      .addCase(fetchReferentCollectionListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchReferentCollectionListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'referentCollections'))
      .addCase(fetchReferentCollectionListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateReferentCollection.pending, handleMutationPending)
      .addCase(sendUpdateReferentCollection.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateReferentCollection.rejected, handleMutationRejected)
      // .addCase(sendUpdateReferentCollection.fulfilled, (state, action) => handleMutationFulfilled(state, action, (newState, action) => {
      //   // by passing this optional callback we now have access to the new state if we want to do something else with it, this works for all reducer handlers
      // }))

      // DELETE
      .addCase(sendDeleteReferentCollection.pending, handleDeletePending)
      .addCase(sendDeleteReferentCollection.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteReferentCollection.rejected, handleDeleteRejected)
  }
});

// export the actions for the reducers defined above
export const { invalidateQuery, invalidateQueries, addReferentCollectionToList, addReferentCollectionsToList } = referentCollectionSlice.actions;


// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// accepts an optional listFetch action so we can use it for other list fetches, defaults to fetchReferentCollectionList
export const fetchListIfNeeded = (queryKey, listFetch = fetchReferentCollectionList) => (dispatch, getState) => {
  const referentCollectionQuery = getState().referentCollection.listQueries[queryKey];
  if(shouldFetch(referentCollectionQuery)) {
    // console.log('Fetching referentCollection list', queryKey);
    dispatch(listFetch(queryKey));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
};

// accepts an optional singleFetch action so we can use it for other single fetches, defaults to fetchSingleReferentCollection
export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleReferentCollection) => (dispatch, getState) => {
  const referentCollectionQuery = getState().referentCollection.singleQueries[id];
  if(shouldFetch(referentCollectionQuery)) {
    dispatch(singleFetch(id));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
}

export default referentCollectionSlice.reducer;
